import { useEffect, useRef, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';

import { useReduxDispatch } from '../../app/store';
import { checkUserLoggedIn, fetchUserDetails } from '../../features/user/userSlice';
import {
  clearDynatraceAuthenticationJourney,
  hasDynatraceAuthenticationAttempt,
  hasDynatraceExpectedAuthenticationSession,
  startDynatraceAuthenticationSessionCheck,
  trackDynatraceAuthenticationFailure,
  trackDynatraceCognitoSessionResult,
  trackDynatraceSignInCancelled,
} from '../../utils/native/dynatrace-events';
import { APP_VERSION, IS_NOT_DEVELOPMENT, IS_PRODUCTION, IS_RUNNING_NATIVE_TESTS } from '../constants';

type CognitoUserWithSession = {
  signInUserSession: {
    idToken: {
      payload: {
        [key: string]: unknown;
      };
    };
  };
};

export function useAppReady() {
  const [isLoaded, setLoaded] = useState(false);
  const dispatch = useReduxDispatch();
  const isFirstRender = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUserStatus = async () => {
      const hasAuthenticationAttempt = hasDynatraceAuthenticationAttempt();
      const hasExpectedSession = hasDynatraceExpectedAuthenticationSession();

      const oauthError = new URLSearchParams(window.location.search).get('error');
      if (oauthError && hasAuthenticationAttempt) {
        if (oauthError === 'access_denied') trackDynatraceSignInCancelled();
        else trackDynatraceAuthenticationFailure('auth_session_unavailable', 'oauth_callback', oauthError);
        setLoaded(true);
        return;
      }

      if (!hasAuthenticationAttempt) startDynatraceAuthenticationSessionCheck();

      const { isLoggedIn, cognitoUser } = unwrapResult(await dispatch(checkUserLoggedIn()));

      if (isLoggedIn) {
        trackDynatraceCognitoSessionResult('success');
      } else if (hasAuthenticationAttempt || hasExpectedSession) {
        trackDynatraceCognitoSessionResult('failure', 'session_unavailable');
      }

      // User is logged into the cIAM session not the newer EIM (EcoStruxure Platform Identity Module) session
      // No choice but to log them out as without a refresh token, we can't get a new access token
      if (isLoggedIn && cognitoUser != null) {
        // NOTE: have to use any type as `CognitoUser` type from amazon-cognito-identity-js does not have correct properties
        const payload = (cognitoUser as unknown as CognitoUserWithSession).signInUserSession.idToken.payload;
        const externalIdpRefreshToken = payload['custom:refresh_token'];
        const externalIdpIdToken = payload['custom:id_token'];
        const hasExternalIdpTokens = !!externalIdpRefreshToken && !!externalIdpIdToken;
        if (!IS_RUNNING_NATIVE_TESTS && !hasExternalIdpTokens) {
          trackDynatraceAuthenticationFailure('external_idp_tokens_missing', 'eim_token_validation');
          navigate('/logout');
          return;
        }
      }

      if (isLoggedIn && isFirstRender.current) {
        isFirstRender.current = false;
        void dispatch(
          fetchUserDetails({
            isProduction: IS_PRODUCTION,
            isNotDevelopment: IS_NOT_DEVELOPMENT,
            version: APP_VERSION,
          })
        ).then((action) => {
          if (!fetchUserDetails.fulfilled.match(action)) clearDynatraceAuthenticationJourney();
        });
      } else if (!isLoggedIn) {
        clearDynatraceAuthenticationJourney();
      }
      setLoaded(true);
    };

    checkUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isLoaded;
}
